import { useEffect, useState } from "react";

import useSingleQuote from "../../data/useSingleQuote";
import { ICalculation, ICalculationData, QuoteType } from "../../models/Quote";
import Card from "../shared/Card/Card";
import PriceFormat from "../../utils/priceFormat";

export const GetQuoteType = (quoteType: QuoteType) => {
  switch (quoteType) {
    case QuoteType.Salvage:
      return "Salvage";
    case QuoteType.ScrapExport:
      return "Scrap Export";
    case QuoteType.SmartPrice:
      return "Smart Price";
  }
};

export default function QuoteCalculationCard({ calculationJson, quoteType, isCollection, total }: { calculationJson: string, quoteType: QuoteType, isCollection: boolean, total: number }) {
  const [calculations, setCalculations] = useState<ICalculation[]>();
  const [dropOff, setDropOff] = useState<number>();
  const [payment, setPayment] = useState<ICalculation[]>([]);

  const CalucationStatusRow = ({ calculation }: { calculation: ICalculation }) => {
    if (!calculation) return null;
    let { name, isFixed, value, baseValue, isAddition, customCalculation } = calculation;

    let output = "";

    if (!value) return null;

    if (customCalculation) {
      output = customCalculation;
    } else if (!isFixed) {
      output = `${value}% of ${PriceFormat(baseValue)} (${PriceFormat(baseValue * value / 100)})`;
    } else {
      output = PriceFormat(value);
    }

    return (
      <div className="py-2 grid grid-cols-2 gap-4">
        <dt className="text-sm font-medium text-gray-500">
          {name}
        </dt>
        <dd className={`items-center text-sm ${isAddition ? '' : 'text-red-600'}`}>
          <div>
            {output}
          </div>
        </dd>
      </div>
    )
  };

  const StatusRow = ({ label, value, classes }: { label: string, value: string, classes: string }) => (
    <div className="py-2 grid grid-cols-2 gap-4">
      <dt className="text-sm font-medium text-gray-500">
        {label}
      </dt>
      <dd className={`items-center text-sm ${classes}`}>
        <div>
          {value}
        </div>
      </dd>
    </div>
  );

  const ExtractCalculationData = (json: string) => {
    let raw = JSON.parse(json);
    if (!raw) return null;
    let data: ICalculationData = {
      salvageCalculations: raw.SalvageCalculations ?? [],
      tradePoorCalculations: raw.TradePoorCalculations ?? [],
      postcodeCalculations: raw.PostcodeCalculations ?? [],
      catalyticCalculations: raw.CatalyticCalculations ?? [],
      engineCalculations: raw.EngineCalculations ?? [],
      vfeCalculations: raw.VfeCalculations ?? [],
      generalCalculations: raw.GeneralCalculations ?? [],
      genericCalculations: raw.GenericCalculations ?? [],
      additionalCalculations: raw.AdditionalCalculations ?? [],
      partsLookupCalculations: raw.PartsLookupCalculations ?? [],
      partsLookupGenericCalculations: raw.PartsLookupGenericCalculations ?? [],
      dropOff: raw.DropOff ?? 0,
      payment: raw.Payment ?? {},
      salvageVoucherCalculation: raw.SalvageVoucherCalculation ?? {},
      scrapVoucherCalculation: raw.ScrapVoucherCalculation ?? {}
    };
    return data;
  };

  const ExtractCalculations = (data: any[]) => {
    let calculations: ICalculation[] = [];

    for (let i = 0; i < data.length; i++) {
      if (data[i]?.DropOff === isCollection) continue;

      calculations.push({
        name: data[i].Name,
        isFixed: data[i].IsFixed,
        value: data[i].Value,
        baseValue: data[i].BaseValue,
        isAddition: data[i].IsAddition,
        dropOff: data[i].DropOff ?? null,
        customCalculation: data[i].CustomCalculation
      });
    }
    return calculations;
  };

  const GetType = (quoteType: QuoteType) => {
    switch (quoteType) {
      case QuoteType.Salvage:
        return "Salvage";
      case QuoteType.ScrapExport:
        return "Scrap Export";
      case QuoteType.SmartPrice:
        return "Smart Price";
    }
  };

  useEffect(() => {

    let data: ICalculationData | null = ExtractCalculationData(calculationJson!);

    if (!data) return;

    switch (quoteType) {
      case QuoteType.Salvage:
        setCalculations([
          ...ExtractCalculations(data.tradePoorCalculations),
          ...ExtractCalculations(data.salvageCalculations),
          ExtractCalculations([data.salvageVoucherCalculation])[0],
        ]);
        break;
      case QuoteType.ScrapExport:
        setCalculations([
          ...ExtractCalculations(data.postcodeCalculations),
          ...ExtractCalculations(data.catalyticCalculations),
          ...ExtractCalculations(data.engineCalculations),
          ...ExtractCalculations(data.vfeCalculations),
          ...ExtractCalculations(data.generalCalculations),
          ...ExtractCalculations(data.genericCalculations),
          ...ExtractCalculations(data.additionalCalculations),
          ExtractCalculations([data.scrapVoucherCalculation])[0],
        ]);
        break;
      case QuoteType.SmartPrice:
        setCalculations([
          ...ExtractCalculations(data.partsLookupCalculations),
          ...ExtractCalculations(data.partsLookupGenericCalculations),
          ExtractCalculations([data.scrapVoucherCalculation])[0],
        ]);
        break;
    }

    setDropOff(data.dropOff);
    setPayment(ExtractCalculations([data.payment]));
  
  }, [calculationJson, quoteType]);

  return (
    <Card bodyClassName="p-4 divide-y divide-gray-200"
      title={`
        ${GetType(quoteType)}
        ${isCollection ? "Collection" : "Drop Off"} 
        Calculation
      `}>
      {calculations != null && calculations?.length > 0 && calculations?.map((calculation, index) => (
        <CalucationStatusRow
          key={index}
          calculation={calculation}
        />
      ))}
      {payment.map((calculation, index) => (
        <CalucationStatusRow
          key={index}
          calculation={calculation}
        />
      ))}
      {!isCollection && dropOff &&
        <StatusRow label="Drop Off" value={PriceFormat(dropOff)} classes="text-gray-900" />
      }

      <hr />
      <StatusRow
        label="Total"
        value={PriceFormat(total)}
        classes="font-medium text-gray-900"
      />
    </Card>
  );
};